import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const AdminNotices: React.FC = () => {
  const { notices, deleteNotice, updateNotice } = useAdmin();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterImportant, setFilterImportant] = useState<boolean | null>(null);
  const [sortBy, setSortBy] = useState<'date' | 'views' | 'title'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedNotices, setSelectedNotices] = useState<string[]>([]);

  // Filtered and sorted Notice
  const filteredAndSortedNotices = useMemo(() => {
    let filtered = notices.filter(notice => {
      const matchesSearch = notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           notice.content.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesImportant = filterImportant === null || notice.isImportant === filterImportant;
      return matchesSearch && matchesImportant;
    });

    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'views':
          aValue = a.views;
          bValue = b.views;
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [notices, searchTerm, filterImportant, sortBy, sortOrder]);

  const handleSelectNotice = (noticeId: string) => {
    setSelectedNotices(prev => 
      prev.includes(noticeId) 
        ? prev.filter(id => id !== noticeId)
        : [...prev, noticeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedNotices.length === filteredAndSortedNotices.length) {
      setSelectedNotices([]);
    } else {
      setSelectedNotices(filteredAndSortedNotices.map(notice => notice.id));
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Would you like to delete the selected ${selectedNotices.length} notice(s)?`)) {
      selectedNotices.forEach(noticeId => deleteNotice(noticeId));
      setSelectedNotices([]);
    }
  };

  const handleToggleImportant = (noticeId: string, currentImportant: boolean) => {
    updateNotice(noticeId, { isImportant: !currentImportant });
  };

  const handleDelete = (noticeId: string, title: string) => {
    if (window.confirm(`Would you like to delete the notice "${title}"?`)) {
      deleteNotice(noticeId);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Notice Management</h1>
          <p className="mt-2 text-sm text-neutral-700">
            {notices.length} notice(s) registered in total.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/admin/notices/new"
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-forest-400 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-forest-500 focus:ring-offset-2"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Notice
          </Link>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label htmlFor="search" className="block text-sm font-medium text-neutral-700 mb-1">
                Search
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-neutral-300 rounded-md leading-5 bg-white placeholder-neutral-500 focus:outline-none focus:placeholder-neutral-400 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
                  placeholder="Title or Content Search..."
                />
              </div>
            </div>

            {/* Important Filter */}
            <div>
              <label htmlFor="important-filter" className="block text-sm font-medium text-neutral-700 mb-1">
                Importance
              </label>
              <select
                id="important-filter"
                value={filterImportant === null ? 'all' : filterImportant.toString()}
                onChange={(e) => {
                  const value = e.target.value;
                  setFilterImportant(value === 'all' ? null : value === 'true');
                }}
                className="block w-full px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
              >
                <option value="all">All</option>
                <option value="true">Important Notice</option>
                <option value="false">Normal Notice</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-neutral-700 mb-1">
                Sort
              </label>
              <div className="flex space-x-2">
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'date' | 'views' | 'title')}
                  className="flex-1 px-3 py-2 border border-neutral-300 rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  <option value="date">Date Created</option>
                  <option value="views">Views</option>
                  <option value="title">Title</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-neutral-300 rounded-md shadow-sm bg-white hover:bg-neutral-50 focus:outline-none focus:ring-forest-500 focus:border-forest-500"
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedNotices.length > 0 && (
        <div className="bg-forest-50 border border-forest-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-forest-800">
              {selectedNotices.length} item(s) selected.
            </span>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Notices Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {filteredAndSortedNotices.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-neutral-900">No notices found</h3>
            <p className="mt-1 text-sm text-neutral-500">Try writing a new notice.</p>
            <div className="mt-6">
              <Link
                to="/admin/notices/new"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-forest-400 hover:bg-forest-500"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Notice
              </Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedNotices.length === filteredAndSortedNotices.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-forest-400 focus:ring-forest-500 border-neutral-300 rounded"
                    />
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Author
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Date Created
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Views
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {filteredAndSortedNotices.map((notice) => (
                  <tr key={notice.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedNotices.includes(notice.id)}
                        onChange={() => handleSelectNotice(notice.id)}
                        className="h-4 w-4 text-forest-400 focus:ring-forest-500 border-neutral-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-neutral-900 max-w-xs truncate">
                              {notice.title}
                            </div>
                            {notice.isImportant && (
                              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Important
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-neutral-500 max-w-xs truncate">
                            {notice.content}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {notice.author}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                      {formatDate(notice.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                      {notice.views.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleImportant(notice.id, notice.isImportant)}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          notice.isImportant
                            ? 'bg-red-100 text-red-800 hover:bg-red-200'
                            : 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200'
                        }`}
                      >
                        {notice.isImportant ? 'Important' : 'Normal'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/admin/notices/${notice.id}/edit`}
                          className="text-forest-400 hover:text-forest-900"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(notice.id, notice.title)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4">
          <h3 className="text-lg font-medium text-neutral-900 mb-4">Statistics</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-forest-400">{notices.length}</div>
              <div className="text-sm text-neutral-500">All Notices</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {notices.filter(n => n.isImportant).length}
              </div>
              <div className="text-sm text-neutral-500">Important Notice</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {notices.reduce((sum, n) => sum + n.views, 0).toLocaleString()}
              </div>
              <div className="text-sm text-neutral-500">Total Views</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {Math.round(notices.reduce((sum, n) => sum + n.views, 0) / notices.length || 0)}
              </div>
              <div className="text-sm text-neutral-500">Avg. Views</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotices;